/**
 * Frøya PDP quantity-tier ATC tagging.
 * Reads shop config from [data-froya-qty-tier-config] (froya_theme_config metaobject).
 * Stamps line properties so the Discount Function can match the promo.
 *
 * Properties:
 *   _froya_promo_id, _froya_purchase_type, _froya_qty_tier
 *
 * Tier % is live-recalculated in the Function from cart qty (pool per purchase type).
 * No _froya_add_instance — lines can merge on 1-by-1 ATC.
 *
 * Standalone — no webpack required. Safe no-op when no matching quantity_tier promo.
 */
(function () {
  'use strict';

  if (window.__FROYA_QTY_TIER_PDP_INIT__) return;
  window.__FROYA_QTY_TIER_PDP_INIT__ = true;

  var PROMO_ID_KEY = '_froya_promo_id';
  var PURCHASE_TYPE_KEY = '_froya_purchase_type';
  var QTY_TIER_KEY = '_froya_qty_tier';
  var ADD_INSTANCE_KEY = '_froya_add_instance';
  var PROXY_URL = '/apps/froya-bundles/config';

  function toVariantGid(id) {
    if (id == null || id === '') return '';
    var raw = String(id);
    if (raw.indexOf('gid://') === 0) return raw;
    return 'gid://shopify/ProductVariant/' + raw;
  }

  function parseConfig(script) {
    if (!script) return null;
    try {
      var parsed = JSON.parse(script.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      console.warn('[froya-qty-tier] Invalid config JSON', e);
      return null;
    }
  }

  function findQtyTierPromo(config, variantGid, templateName) {
    if (!config || !variantGid) return null;
    var bundles = Array.isArray(config.bundles) ? config.bundles : [];
    var match = null;

    for (var i = 0; i < bundles.length; i++) {
      var promo = bundles[i];
      if (!promo || promo.promoType !== 'quantity_tier') continue;
      if (promo.active === false) continue;

      var ids = Array.isArray(promo.variantIds) ? promo.variantIds : [];
      if (ids.indexOf(variantGid) === -1) continue;

      var promoTemplate = typeof promo.template === 'string' ? promo.template.trim() : '';
      if (promoTemplate && promoTemplate !== templateName) continue;

      // Prefer template-specific over site-wide
      if (promoTemplate) return promo;
      if (!match) match = promo;
    }

    return match;
  }

  function getRoot(script) {
    return script.closest('c-subscription') || script.closest('.c-product-form') || document;
  }

  function getForm(root) {
    return (
      root.querySelector('form.js-subscribtion__subscription-form') ||
      root.querySelector('form.c-subscribtion__subscription-form') ||
      root.querySelector('form')
    );
  }

  function readPurchaseType(form, root) {
    var checked =
      (form && form.querySelector('input[name="purchase_type"]:checked')) ||
      (root && root.querySelector('input[name="purchase_type"]:checked'));
    if (checked && checked.value) return checked.value === 'subscription' ? 'subscription' : 'one_time';
    return 'one_time';
  }

  function readQuantity(form) {
    if (!form) return 1;
    var qtyRadio = form.querySelector('input[name="quantity"]:checked');
    if (qtyRadio && qtyRadio.value) {
      var n = parseInt(qtyRadio.value, 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }
    var qtyInput = form.querySelector('input[name="quantity"]');
    if (qtyInput && qtyInput.value) {
      var q = parseInt(qtyInput.value, 10);
      return Number.isFinite(q) && q > 0 ? q : 1;
    }
    return 1;
  }

  function readVariantId(form, fallback) {
    if (!form) return fallback;
    var checked =
      form.querySelector('input.js-variant-quantity-selector:checked') ||
      form.querySelector('input[name="id"]:checked') ||
      form.querySelector('input[name="id"]');
    if (checked && checked.value) return checked.value;
    return fallback;
  }

  function setPropertyInput(form, key, value) {
    if (!form) return;
    var name = 'properties[' + key + ']';
    var input = form.querySelector('input[name="' + name.replace(/"/g, '\\"') + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    if (value == null || value === '') {
      input.remove();
      return;
    }
    input.value = String(value);
  }

  function clearQtyTierProperties(form) {
    setPropertyInput(form, PROMO_ID_KEY, '');
    setPropertyInput(form, PURCHASE_TYPE_KEY, '');
    setPropertyInput(form, QTY_TIER_KEY, '');
    setPropertyInput(form, ADD_INSTANCE_KEY, '');
  }

  function stampQtyTierProperties(form, root, config, templateName, fallbackVariantId) {
    if (!form) return null;

    var variantId = readVariantId(form, fallbackVariantId);
    var variantGid = toVariantGid(variantId);
    var promo = findQtyTierPromo(config, variantGid, templateName);

    if (!promo) {
      clearQtyTierProperties(form);
      form.removeAttribute('data-froya-qty-tier-active');
      return null;
    }

    // Pack / variant-qty mode uses quantity=1 at ATC — skip tagging (different product model)
    if (root && root.getAttribute('data-use-variant-quantities') === 'true') {
      clearQtyTierProperties(form);
      form.removeAttribute('data-froya-qty-tier-active');
      return null;
    }

    var purchaseType = readPurchaseType(form, root);
    var qty = readQuantity(form);

    setPropertyInput(form, PROMO_ID_KEY, promo.id);
    setPropertyInput(form, PURCHASE_TYPE_KEY, purchaseType);
    setPropertyInput(form, QTY_TIER_KEY, String(qty));
    // Drop legacy per-add UUID so identical ATC lines can merge
    setPropertyInput(form, ADD_INSTANCE_KEY, '');

    form.setAttribute('data-froya-qty-tier-active', 'true');
    form.setAttribute('data-froya-qty-tier-promo', promo.id);
    return promo;
  }

  function bindForm(script, config) {
    var templateName = script.getAttribute('data-froya-template') || '';
    var fallbackVariantId = script.getAttribute('data-froya-variant-id') || '';
    var root = getRoot(script);
    var form = getForm(root);
    if (!form || form.__froyaQtyTierBound) return;
    form.__froyaQtyTierBound = true;

    var sync = function () {
      stampQtyTierProperties(form, root, config, templateName, fallbackVariantId);
    };

    sync();

    form.addEventListener(
      'change',
      function (event) {
        var t = event.target;
        if (!t) return;
        if (
          t.name === 'quantity' ||
          t.name === 'purchase_type' ||
          t.name === 'id' ||
          t.classList.contains('js-variant-quantity-selector')
        ) {
          sync();
        }
      },
      true,
    );

    form.addEventListener('submit', sync, true);
  }

  function initWithConfig(config) {
    if (!config) return;
    var scripts = document.querySelectorAll('script[data-froya-qty-tier-config]');
    scripts.forEach(function (script) {
      var local = parseConfig(script) || config;
      bindForm(script, local);
    });
  }

  function boot() {
    var scripts = document.querySelectorAll('script[data-froya-qty-tier-config]');
    if (!scripts.length) return;

    var firstConfig = parseConfig(scripts[0]);
    if (firstConfig) {
      initWithConfig(firstConfig);
      return;
    }

    // Fallback: app proxy (same as buy box)
    fetch(PROXY_URL, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (json) {
        if (json) initWithConfig(json);
      })
      .catch(function () {
        /* ignore */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind if theme re-renders product form
  if (typeof MutationObserver !== 'undefined') {
    var obs = new MutationObserver(function () {
      var unbound = document.querySelector(
        'script[data-froya-qty-tier-config]',
      );
      if (!unbound) return;
      var root = getRoot(unbound);
      var form = getForm(root);
      if (form && !form.__froyaQtyTierBound) boot();
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
